// src/components/PartnersPicker/hooks/usePartners.ts
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser } from 'store/profile';
import { handleGetPartners } from 'store/partner';
import { updateUser } from 'store/user';
import { getBaseReactUrl } from 'utils/pathUtils';
import { partnerSelectors } from '../selectors';
import debounce from 'lodash/debounce';

export const usePartners = () => {
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const currentUser = useSelector(getCurrentUser);
  const partners = useSelector(partnerSelectors.getPartners);
  const { totalPages, totalCount } = useSelector(partnerSelectors.getMeta);

  // Create a debounced search function that updates the debouncedSearchText
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((text) => {
      setDebouncedSearchText(text);
      setPageNumber(1); // Reset to first page on search
    }, 300),
    []
  );

  // Handle input changes
  const handleSearch = (value) => {
    setSearchText(value);
    setIsSearching(true);
    debouncedSearch(value);
  };

  // Effect for fetching partners when debounced search or page changes
  useEffect(() => {
    const fetchPartners = async () => {
      setIsLoading(true);
      await dispatch(handleGetPartners(pageNumber, debouncedSearchText));
      setIsLoading(false);
      setIsSearching(false);
    };

    fetchPartners();
  }, [pageNumber, debouncedSearchText, dispatch]);

  const goToPage = (page) => {
    setPageNumber(page);
  };

  const selectPartner = async (partnerId) => {
    await dispatch(updateUser({ primary_partner_id: partnerId }, currentUser.id));
    window.location.assign(`${getBaseReactUrl()}/app/nav/v4/dashboard`);
  };

  return {
    partners,
    searchText,
    debouncedSearchText,
    pageNumber,
    totalPages,
    totalCount,
    isLoading,
    isSearching,
    handleSearch,
    selectPartner,
    goToPage,
  };
};
